import { Box, Grid, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** ROUTE */
import { routeName } from 'routes/routes-name'

/** UTILS || SLICE */

/** API */
import { fileUrl } from 'api/api'
import NewsApi, { NewsType, ObjImage } from 'api/application/news.api'

/** COMPONENT */
import HeaderLabel from 'component/CardCustom/HeaderLabel'
import InputSecondNewDatePicker from 'component/Input/InputSecondNewDatePicker'
import InputTextEditor from 'component/Input/InputTextEditor'
import InputTimePicker from 'component/Input/InputTimePicker'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import UploadImage from 'component/Upload/UploadImage'
import { DateObject } from 'react-multi-date-picker'

/** STYLE */
import { colors } from 'constants/theme'
import * as UseStyled from 'features/application/promotions/use-styled'
import SwitchButton from 'new-components/buttons/switch-button'
import TextInput from 'new-components/inputs/text-input'

type ErrorMessage = {
  EMPTY_SUBJECT?: string
  EMPTY_DATE_PUBLIC?: string
  EMPTY_TIME_PUBLIC?: string
  EMPTY_TIME_END?: string
  EMPTY_IMAGE?: string
  EMPTY_CONTENT?: string
}

export default function FormNews() {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const maxHeight = 450
  const maxWidth = 700

  const objImage: ObjImage = {
    fileId: null,
    base64: '',
    type: '',
    fileExtension: '',
    fileSize: ''
  }

  const [newsId, setNewsId] = useState<number>(0);
  const [news, setNews] = useState<NewsType>(
    {
      subject: '',
      content: '',
      datePublic: '',
      timePublic: '',
      dateEnd: '',
      timeEnd: '',
      image: objImage
    })
  const [previewImage, setPreviewImage]: any = useState()
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>({});
  const [isDeleteImage, setIsDeleteImage] = useState(false);

  const loadData = async (id: number) => {
    await NewsApi.findById(id).then((resp) => {
      if (!_.isEmpty(resp.data)) {
        const data: any = resp.data
        setNews({
          subject: data.subject,
          content: data.content,
          datePublic: data.datePublic,
          timePublic: data.timePublic ? data.timePublic.substring(0, 5) : '',
          dateEnd: data.dateEnd,
          timeEnd: data.timeEnd ? data.timeEnd.substring(0, 5) : '',
          status: data.status,
          image: {
            fileId: data?.image?.fileId || 0,
            base64: data?.image?.base64 || '',
            type: data?.image?.type || '',
            fileExtension: data?.image?.fileExtension || '',
            fileSize: data?.image?.fileSize || ''
          }
        })
        setPreviewImage(`${fileUrl}/${data?.image.filePath}/${data?.image.fileName}`)
      }
    }).catch((e) => {
      return
    })
  }

  const submit = () => {
    const checkImg = (!isDeleteImage && newsId && !news.image.fileId) || (isDeleteImage && newsId && !news.image.base64) || (!newsId && !news.image.base64)
    const checkContent = !news.content || news.content === `<p></p>\n` || news.content === `<p>&nbsp;</p>\n`
    if (!news.subject || !news.datePublic || news.datePublic === '0000-00-00' || !news.timePublic || news.timePublic === '00:00:00' || (news.dateEnd && (!news.timeEnd || news.timeEnd === '00:00:00')) || checkImg || checkContent) {
      return setErrorMessage({
        EMPTY_SUBJECT: !news.subject ? t('NEWS.MESSAGE.ERROR.EMPTY_SUBJECT') : '',
        EMPTY_DATE_PUBLIC: !news.datePublic || news.datePublic === '0000-00-00' ? t('NEWS.MESSAGE.ERROR.EMPTY_DATE_PUBLIC') : '',
        EMPTY_TIME_PUBLIC: !news.timePublic || news.timePublic === '00:00:00' ? t('NEWS.MESSAGE.ERROR.EMPTY_TIME_PUBLIC') : '',
        EMPTY_TIME_END: (news.dateEnd && (!news.timeEnd || news.timeEnd === '00:00:00')) ? t('NEWS.MESSAGE.ERROR.EMPTY_TIME_END') : '',
        EMPTY_IMAGE: checkImg ? `${t('NEWS.MESSAGE.ERROR.EMPTY_IMAGE')}` : '',
        EMPTY_CONTENT: checkContent ? `${t('NEWS.MESSAGE.ERROR.EMPTY_CONTENT')}` : ''
      })
    } else {
      newsId ? update() : create()
    }
  }

  useEffect(() => {
    if (news.image.base64) {
      setPreviewImage(`data:image/jpeg;base64,${news.image.base64}`);
    }
  }, [news.image.base64]);


  const create = async () => {
    await NewsApi.createNews(news).then((e) => {
      notiSuccess(t('NEWS.MESSAGE.SUCCESS.CREATE'))
      history.push(routeName.news)
    }).catch((e) => {
      notiError(t('NEWS.MESSAGE.ERROR.DEFAULT'))
    })
  }



  const update = async () => {
    try {
      if (isDeleteImage || news.image.base64) {
        await uploadNewsImage(news.image);
      }
      await NewsApi.updateNews(newsId, news);
      notiSuccess(t('NEWS.MESSAGE.SUCCESS.UPDATE'));
      history.push(routeName.news);
    } catch (e) {
      notiError(t('NEWS.MESSAGE.ERROR.DEFAULT'));
    }
  };


  const uploadNewsImage = (files: any) => {
    if (files && files[0]) {
      const rawFile = files[0];
      const fileName = rawFile.name.split('.');

      const file: any = new window.Image();
      file.src = window.URL.createObjectURL(rawFile);

      file.onload = () => {
        if (file.width > maxWidth || file.height > maxHeight) {
          return notiError(t('NEWS.FORM.IMAGE_SIZE'));
        } else {
          const reader: any = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',');
            const base64String = base64.length > 0 ? base64[base64.length - 1] : reader.result;
            setNews(prev => {
              setIsDeleteImage(true);
              const fileId = prev.image?.fileId && prev.image.fileId > 0
                ? 0
                : prev.image?.fileId || 0;

              const detailImage = {
                fileId: fileId,
                base64: base64String,
                type: '',
                fileExtension: fileName[fileName.length - 1],
                fileSize: rawFile.size.toString(),
              };

              return {
                ...prev,
                image: detailImage,
              };
            });


            setPreviewImage(reader.result);
          };

          reader.readAsDataURL(rawFile);
        }
      };
    }
  };


  const clearErrorMessage = () => {
    setErrorMessage({})
  }

  useEffect(() => {
    const valueNewsId: any = location.state
    if (!_.isEmpty(valueNewsId) && valueNewsId.newsId) {
      loadData(valueNewsId.newsId)
      setNewsId(valueNewsId.newsId)
    }
  }, [location])

  const renderTimePicker = (date: any, time?: string) => {
    if (moment(date).isSame(moment(), 'day')) {
      const newDate = moment().toString().split(" ")
      if (time && time !== "" && newDate[4]) {
        newDate[4] = `${time}:00`
      }
      return newDate.join(" ")
    }
    return ''
  }

  const handleTimeOfFive = (hour: number, num: number): { hour: number, minute: number | '00' } => {
    if (num === 60) {
      return { hour: hour + 1, minute: `00` }
    }

    if (num % 5 === 0) {
      return { hour, minute: num };
    } else if (num > 55 && num < 59) {
      return { hour, minute: 55 }
    }

    return { hour, minute: Math.ceil(num / 5) * 5 }
  };

  const handleChangeTimePicker = (date: any, value: string, time?: string) => {
    const dateCurrent = renderTimePicker(date, time)
    if (dateCurrent !== '') {
      const newValue = value?.split(':')
      if (moment(dateCurrent).hour() <= Number(newValue[0])) {
        if (moment(dateCurrent).hour() === Number(newValue[0]) && !(moment(dateCurrent).minute() <= Number(newValue[1]))) {
          const { hour, minute } = handleTimeOfFive(Number(newValue[0]), moment(dateCurrent).minute())
          return `${hour}:${minute}`
        }
        return value
      } else {
        return ''
      }
    }
    return value
  }


  return (
    <Box height="100%" sx={{ overflow: 'hidden' }}>
      <Box height="100%" display="flex" flexDirection="column">
        <HeaderLabel
          text={`${newsId ? t('NEWS.FORM.FORM_EDIT') : t('NEWS.FORM.FORM_ADD')}`}
          navigator={{ previousTo: routeName.news, previousTitle: t('NEWS.HEADER'), currentTitle: newsId ? t('NEWS.FORM.FORM_EDIT') : t('NEWS.FORM.FORM_ADD') }}
          goBack={routeName.news}
          onSubmit={submit}
        />
        <Box border="1px solid" borderColor={colors.lightGray} boxShadow={1} mx={3} mb={3} height="100%" overflow="hidden" borderRadius={'8px'} bgcolor="#fff">
          <Box height="100%" py={0} pl={2} pr={3} overflow="auto" display="flex" flexDirection="column" >
            <Grid container spacing={2} mt={2} mb={2}>
              <Grid container spacing={2} pl={2} display="flex" justifyContent="space-between">
                <Grid item xs={12} sm={6} md={5} xl={4}>
                  <TextInput
                    required
                    label={t('NEWS.FORM.TITLE')}
                    value={news.subject}
                    onChange={(e: any) => {
                      setNews({ ...news, subject: e.target.value })
                      clearErrorMessage()
                    }}
                    helperText={errorMessage.EMPTY_SUBJECT || ''}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={7} xl={8}>
                  {newsId ? (
                    <Box display="flex" justifyContent="flex-end">
                      <Box display="flex" justifyContent="space-between">
                        <Typography marginRight={2}>{t('INPUT.LABEL_STATUS')}</Typography>
                        <SwitchButton onChange={() => setNews({ ...news, status: news.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' })} checked={news.status === 'ACTIVE' ? true : false} />
                      </Box>
                      {/* <Divider sx={{ display: { xl: 'none' }, backgroundColor: 'transparent', borderColor: `${colors.lightGray} !important` }} /> */}
                    </Box>
                  ) : (<></>)}
                </Grid>
              </Grid>
              <Grid item width={'100%'}>
                <Typography className='mt-2' sx={{ fontSize: 18, fontWeight: 500 }}>{t('PROMOTIONS.FORM.TITLE_DATE')}</Typography>
              </Grid>
              <Grid item width={'100%'}>
                <Grid container spacing={2}>
                  <Grid item xs={7} md={5} lg={4} width={'100%'}>
                    <InputSecondNewDatePicker
                      required
                      label={t('PROMOTIONS.FORM.DATE_START')}
                      value={news.datePublic && news.datePublic !== '0000-00-00' ? news.datePublic : ''}
                      onchange={(e: any) => {
                        setNews({ ...news, datePublic: moment(e).format('YYYY-MM-DD'), timePublic: '' })
                        clearErrorMessage()
                      }}
                      helperText={errorMessage.EMPTY_DATE_PUBLIC || ''}
                      disablePast
                      minDate={new DateObject()}
                      disableFuture
                      maxDate={news.dateEnd || ''}
                    />
                  </Grid>
                  <Grid item xs={5} md={3} lg={3} width={'100%'}>
                    <InputTimePicker
                      required
                      key={'timePublic'}
                      label={t('PROMOTIONS.FORM.TIME_START')}
                      value={news.timePublic && news.timePublic !== '00:00:00' ? news.timePublic : ''}
                      helperText={errorMessage.EMPTY_TIME_PUBLIC || ''}
                      onChange={(e: any) => {
                        const value = moment(e).format('HH:mm')
                        const newValue = handleChangeTimePicker(news.datePublic, value)
                        setNews({ ...news, timePublic: newValue, timeEnd: '' })
                        clearErrorMessage()
                      }}
                      scopeTimeStart={renderTimePicker(news.datePublic)}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item width={'100%'}>
                <Grid container spacing={2}>
                  <Grid item xs={7} md={5} lg={4} width={'100%'}>
                    <InputSecondNewDatePicker
                      allowClear
                      label={t('PROMOTIONS.FORM.DATE_END')}
                      value={news.dateEnd && news.dateEnd !== '0000-00-00' ? news.dateEnd : ''}
                      disablePast
                      datePublic={news.datePublic && news.datePublic !== '0000-00-00' ? news.datePublic : ''}
                      onchange={(e: any) => {
                        setNews({ ...news, dateEnd: moment(e).format('YYYY-MM-DD'), timeEnd: '' })
                        clearErrorMessage()
                      }}
                      onClear={() => {
                        setNews({ ...news, dateEnd: '', timeEnd: '' })
                        clearErrorMessage()
                      }}
                      minDate={news.datePublic ? new DateObject(news.datePublic) : new DateObject()}
                    />
                  </Grid>
                  <Grid item xs={5} md={3} lg={3} width={'100%'}>
                    <InputTimePicker
                      key={'timeEnd'}
                      label={t('PROMOTIONS.FORM.TIME_END')}
                      value={news.timeEnd && news.timeEnd !== '00:00:00' ? news.timeEnd : ''}
                      onChange={(e: any) => {
                        const value = moment(e).format('HH:mm')
                        const newValue = handleChangeTimePicker(news.dateEnd, value, news.timePublic)
                        setNews({ ...news, timeEnd: newValue })
                        clearErrorMessage()
                      }}
                      helperText={errorMessage.EMPTY_TIME_END || ''}
                      scopeTimeStart={news?.dateEnd ? renderTimePicker(news.dateEnd, news.timePublic) : ''}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item width={'100%'}>
                <Typography className='mt-2' sx={{ fontSize: 18, fontWeight: 500 }}>{t('PROMOTIONS.FORM.IMAGE')}</Typography>
              </Grid>
              <Grid item width={'100%'}>
                <UseStyled.UploadImage>
                  <UploadImage
                    setValue={uploadNewsImage}
                    pathFile={previewImage}
                    canDelete
                    onDelete={() => {
                      setNews(prev => ({
                        ...prev,
                        image: objImage
                      }));
                      setIsDeleteImage(true);
                      setPreviewImage(null);
                    }}
                  />
                </UseStyled.UploadImage>
                <Typography className='mt-1' sx={{ fontSize: 16, color: colors.disabledGray }}>({t('PROMOTIONS.FORM.IMAGE_SIZE')})</Typography>
              </Grid>
              <Grid item width={'100%'}>
                <Typography className='mt-2' sx={{ fontSize: 18, fontWeight: 500 }}>{t('PROMOTIONS.FORM.CONTENT')}</Typography>
              </Grid>
              <Grid item width={'100%'}>
                {(newsId && news.content) && (
                  <InputTextEditor
                    value={news.content}
                    setValue={(val: string) => setNews({ ...news, content: val })}
                  />
                ) || <></>}
                {(!newsId) && (
                  <InputTextEditor
                    setValue={(val: string) => setNews({ ...news, content: val })}
                  />
                ) || <></>}
              </Grid>
            </Grid>
          </Box >
        </Box >
      </Box>
    </Box >
  )
}
